<template>
  <div ref="range" class="range-input">
    <label class="input-wrap">
      <span v-if="firstPrefix" class="prefix">
        {{ firstPrefix }}
      </span>
      <input
        ref="min"
        class="input-number body-m-regular"
        :value="internalVal.min"
        :placeholder="settings.min_value"
        type="tel"
        @blur="onChange($event.target, 'min')"
        @input="onInput($event.target, 'min')"
        @keyup.enter="getFilters('min')"
        @keypress="onPress"
      />
    </label>
    <label class="input-wrap">
      <span v-if="secondPrefix" class="prefix">
        {{ secondPrefix }}
      </span>
      <input
        ref="max"
        class="input-number body-m-regular"
        :value="internalVal.max"
        :placeholder="settings.max_value"
        type="tel"
        @blur="onChange($event.target, 'max')"
        @input="onInput($event.target, 'max')"
        @keyup.enter="getFilters('max')"
        @keypress="onPress"
      />
    </label>
  </div>
</template>

<script setup lang="ts">
import type { RangeFilter } from '~/interfaces/api';
import type { RangeFilteringValue, internalValue } from '~/interfaces/app';

const props = defineProps({
  settings: {
    type: Object as () => RangeFilter,
    required: true,
  },
  value: {
    type: Object as () => RangeFilteringValue,
    required: true,
  },
  firstPrefix: {
    type: String,
    default: 'от',
  },
  secondPrefix: {
    type: String,
    default: 'до',
  },
  emitPosition: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['position', 'onChange', 'get-filters']);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const range = ref<HTMLElement>();
const min = ref<HTMLInputElement>();
const max = ref<HTMLInputElement>();
const internalVal = computed<internalValue>({
  get: (): internalValue => ({
    min:
      props.value?.min && props.value?.min !== props.settings.min_value
        ? props.value?.min
        : null,
    max:
      props.value?.max && props.value?.max !== props.settings.max_value
        ? props.value?.max
        : null,
  }),
  set: () => {},
});

const onChange = (target: HTMLInputElement, key: 'min' | 'max'): void => {
  if (debounceTimer) clearTimeout(debounceTimer);
  const floatVal = parseFloat(target.value.replace(',', '.'));
  if (floatVal !== props.value[key]) {
    const newVal: RangeFilteringValue = { ...props.value };
    newVal[key] = isNaN(floatVal)
      ? props.settings[key === 'min' ? 'min_value' : 'max_value']
      : floatVal;
    const queryVal =
      (newVal.min === props.settings.min_value &&
        newVal.max === props.settings.max_value) ||
      newVal.max <= newVal.min
        ? undefined
        : `${newVal?.min},${newVal?.max}`;

    if (props.value?.min !== newVal?.min || props.value?.max !== newVal?.max) {
      emit('onChange', {
        id: props.settings.id ? props.settings.id.toString() : 0,
        val: newVal,
        queryVal,
      });
    }

    if (props.emitPosition && range.value) {
      const { right, top, height } = range.value.getBoundingClientRect();
      emit('position', { right, top: top + height / 2 + window.scrollY });
    }
  } else target.value = String(floatVal || 0);
};

function onInput(target: HTMLInputElement, key: 'min' | 'max'): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  if (
    props.settings.precision
      ? !/^[0-9]*([.|,][0-9]*)$/g.test(target.value)
      : !/^[0-9]*$/g.test(target.value)
  ) {
    const floatVal = parseFloat(target.value.replace(',', '.'));
    if (!isNaN(floatVal))
      target.value = String(+floatVal.toFixed(props.settings.precision || 0));
  }
  if (
    target.value.length &&
    !/^[.|,]$/.test(target.value.substr(target.value.length - 1))
  )
    debounceTimer = setTimeout(() => onChange(target, key), 500);
}

const onPress = (e: KeyboardEvent): void => {
  const target = e.target as HTMLInputElement;
  if (target.value.length > 12 || !e.key?.match(/^[.,]|\d+$/))
    e.preventDefault();

  const isComma = !!e.key?.match(/^[.,]/);

  if (props.settings.precision) {
    const matchComma = target.value.match(/[.,]/);
    if (
      matchComma &&
      (isComma ||
        target.value.length - matchComma.index > props.settings.precision)
    )
      e.preventDefault();
  } else if (isComma) e.preventDefault();
};

const getFilters = (input: string) => {
  if (min.value && max.value) {
    if (input === 'min') {
      min.value?.blur();
    } else {
      max.value?.blur();
    }
    if (
      parseFloat(min.value?.value) > parseFloat(max.value?.value) ||
      parseFloat(min.value?.value) > props.settings.max_value
    ) {
      min.value.value = String(props.settings.min_value);
      onChange(min.value, 'min');
    }
    if (
      parseFloat(max.value?.value) < parseFloat(min.value?.value) ||
      parseFloat(max.value?.value) < props.settings.min_value
    ) {
      max.value.value = String(props.settings.max_value);
      onChange(max.value, 'max');
    }
    setTimeout(() => emit('get-filters'), 50);
  }
};
</script>

<style scoped lang="scss" src="./range-input.scss" />
